<template>
    <Container>
        <Switch
            v-model="allowNotifications"
            @change="toggleNotifications">
            {{ $t('Notifications.allowNotifications') }}
        </Switch>
        <template
            v-for="(category, i) in filteredCategories"
            v-if="allowNotifications"
            :key="category.wordpressId">
            <Checkbox
                v-model="subscribedCategories"
                :value="category.wordpressId"
                @change="subscribeToCategory">
                <CategoryName
                    :category="category"
                    class="!tw-font-medium"
                    :class="category.textColor" />
            </Checkbox>
            <v-divider
                v-if="i < filteredCategories.length - 1"
                class="tw-mx-2"
                color="gray-300"
                opacity="1">
            </v-divider>
        </template>
        <div
            v-if="isLoading || hasError || hasUpdated || noPermission"
            class="tw-mx-auto tw-mt-4 tw-flex tw-max-w-72 tw-items-center tw-justify-center tw-text-center">
            <v-progress-circular
                v-if="isLoading"
                color="primary"
                indeterminate
                size="24"
                width="2">
            </v-progress-circular>
            <span
                v-if="hasUpdated"
                class="tw-text-sm tw-font-medium tw-text-green-600">
                {{ $t('Notifications.updated') }}
            </span>
            <span
                v-if="hasError"
                class="tw-text-sm tw-font-medium tw-text-red">
                {{ $t('Notifications.subscribeError') }}
            </span>
            <span
                v-if="noPermission"
                class="tw-text-sm tw-font-medium tw-text-red">
                {{ $t('Notifications.noPermission') }}
            </span>
        </div>
    </Container>
</template>

<script setup lang="ts">
    import Container from '@/components/shared/Container.vue';
    import Switch from '@/components/shared/Switch.vue';
    import Checkbox from '@/components/shared/Checkbox.vue';
    import categories from '@/configs/categories';
    import useNotifications from '@/composables/useNotifications';
    import { useNotificationsStore } from '@/store/notifications';
    import _ from 'lodash';
    import { subscribeToCategories } from '@/api/posts';

    const notifications = useNotifications();
    const notificationsStore = useNotificationsStore();

    const isLoading = ref(false);
    const hasUpdated = ref(false);
    const hasError = ref(false);
    const noPermission = ref(false);
    const allowNotifications = ref(false);
    const subscribedCategories = ref<number[]>([]);
    const deviceToken = computed(() => notificationsStore.getNotificationsToken);

    const filteredCategories = categories.filter((category) => category.showInNotifications);

    const debouncedSubscribeToCategories = _.debounce(async () => {
        isLoading.value = true;
        hasUpdated.value = false;
        hasError.value = false;
        try {
            await requestPermission();
            if (!deviceToken.value) {
                noPermission.value = true;
                return;
            }
            await subscribeToCategories(toRaw(deviceToken.value), toRaw(subscribedCategories.value));
            hasUpdated.value = true;
            notificationsStore.setSubscribedCategories(_.cloneDeep(toRaw(subscribedCategories.value)));
            setTimeout(() => {
                hasUpdated.value = false;
            }, 3000);
        } catch (error) {
            console.error(error);
            hasError.value = true;
        } finally {
            isLoading.value = false;
        }
    }, 500);

    async function toggleNotifications(value: boolean) {
        if (!value) {
            if (deviceToken.value) {
                await subscribeToCategories(toRaw(deviceToken.value), []);
            }
            subscribedCategories.value = [];
            notificationsStore.setNotificationsToken('');
            notificationsStore.resetSubscribedCategories();
            return;
        }

        await requestPermission();
    }

    async function requestPermission() {
        if (deviceToken.value) {
            return;
        }
        const token = await notifications.requestPermission();
        notificationsStore.setNotificationsToken(token as string);
    }

    async function subscribeToCategory() {
        isLoading.value = true;
        debouncedSubscribeToCategories();
    }

    function init() {
        allowNotifications.value = notificationsStore.getNotificationsToken !== '';
        subscribedCategories.value = _.cloneDeep(notificationsStore.getSubscribedCategories);
    }

    init();
</script>

<style scoped></style>
